import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { holidayService } from '@/api/domains/attendance';
import type { HolidayByRegionItem } from '@/api/domains/attendance';
import { holidayTypeCatalogService } from '@/api/domains/catalog/holidayType';
import type { HolidayType } from '@/api/domains/catalog/holidayType';
import { geographyService } from '@/api/domains/geography';
import type { Region } from '@/api/domains/geography';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { formatDateToYYYYMMDD } from '@/utils';
import {
  holidayByRegionFormSchema,
  type HolidayByRegionFormData,
} from '@/schemas/attendance/holidayByRegionForm';


interface UseHolidayByRegionFormPanelParams {
  holidayByRegionData?: HolidayByRegionItem;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export interface UseHolidayByRegionFormPanelResult {
  isEditMode: boolean;
  holidayId: number | null;
  holidayTypeValue: string;
  holidayTypeOptions: { value: string; label: string }[];
  regionValue: string;
  regionOptions: { value: string; label: string }[];
  dateValue: string;
  reasonValue: string;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  errors: ReturnType<typeof useForm<HolidayByRegionFormData>>['formState']['errors'];
  isSubmitted: boolean;
  handleHolidayTypeChange: (value: string | string[]) => void;
  handleRegionChange: (value: string | string[]) => void;
  handleDateChange: (value: string) => void;
  handleReasonChange: (value: string) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export function useHolidayByRegionFormPanel({
  holidayByRegionData,
  onSubmit,
  readOnly,
}: UseHolidayByRegionFormPanelParams): UseHolidayByRegionFormPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, showError, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  const isEditMode = !!holidayByRegionData;
  const effectiveReadOnly = !!readOnly;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const initialDate = useMemo(
    () =>
      holidayByRegionData
        ? formatDateToYYYYMMDD(new Date(holidayByRegionData.date))
        : formatDateToYYYYMMDD(new Date()),
    [holidayByRegionData],
  );

  const [holidayTypeValue, setHolidayTypeValue] = useState<string>(
    holidayByRegionData ? String(holidayByRegionData.holidayTypeId) : '',
  );
  const [regionValue, setRegionValue] = useState<string>(
    holidayByRegionData ? String(holidayByRegionData.regionId) : '',
  );
  const [dateValue, setDateValue] = useState<string>(initialDate);
  const [reasonValue, setReasonValue] = useState<string>(holidayByRegionData?.reason ?? '');

  const holidayTypeCatalog = useCatalogLoader<HolidayType>(
    (signal) => holidayTypeCatalogService.getHolidayTypes(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:holiday.messages.loadError')),
  );

  const regionCatalog = useCatalogLoader<Region>(
    (_signal) => geographyService.getRegions('CL'),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:holiday.messages.loadError')),
  );

  useEffect(() => {
    setLoading(!holidayTypeCatalog.isLoaded || !regionCatalog.isLoaded);
  }, [holidayTypeCatalog.isLoaded, regionCatalog.isLoaded, setLoading]);

  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<HolidayByRegionFormData>({
    resolver: zodResolver(holidayByRegionFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      holidayTypeId: holidayByRegionData?.holidayTypeId ?? null,
      regionId: holidayByRegionData?.regionId ?? null,
      date: initialDate,
      reason: holidayByRegionData?.reason ?? '',
    },
  });

  useEffect(() => {
    register('holidayTypeId');
    register('regionId');
    register('date');
    register('reason');

    return () => {
      unregister('holidayTypeId');
      unregister('regionId');
      unregister('date');
      unregister('reason');
    };
  }, [register, unregister]);

  useEffect(() => {
    setValue('holidayTypeId', holidayTypeValue ? Number(holidayTypeValue) : null);
  }, [holidayTypeValue, setValue]);

  useEffect(() => {
    setValue('regionId', regionValue ? Number(regionValue) : null);
  }, [regionValue, setValue]);

  useEffect(() => {
    setValue('date', dateValue);
  }, [dateValue, setValue]);

  useEffect(() => {
    setValue('reason', reasonValue);
  }, [reasonValue, setValue]);

  const isReady = useMemo(
    () =>
      holidayTypeCatalog.isLoaded &&
      regionCatalog.isLoaded &&
      holidayTypeValue.length > 0 &&
      regionValue.length > 0 &&
      dateValue.length > 0 &&
      reasonValue.trim().length > 0,
    [
      holidayTypeCatalog.isLoaded,
      regionCatalog.isLoaded,
      holidayTypeValue,
      regionValue,
      dateValue,
      reasonValue,
    ],
  );

  const handleHolidayTypeChange = useCallback((value: string | string[]) => {
    setHolidayTypeValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleRegionChange = useCallback((value: string | string[]) => {
    setRegionValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleDateChange = useCallback((value: string) => {
    setDateValue(value);
  }, []);

  const handleReasonChange = useCallback((value: string) => {
    setReasonValue(value);
  }, []);

  const onValidSubmit = useCallback(
    async (values: HolidayByRegionFormData) => {
      if (effectiveReadOnly) return;

      setIsSubmitting(true);
      try {
        const payload = {
          holidayTypeId: values.holidayTypeId as number,
          regionId: values.regionId as number,
          date: values.date,
          reason: values.reason,
        };

        const response = isEditMode && holidayByRegionData
          ? await holidayService.updateByRegion(holidayByRegionData.id, payload)
          : await holidayService.createByRegion(payload);

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }

          showApiError(
            response,
            translate(
              isEditMode
                ? 'attendance:holiday.messages.updateByRegionError'
                : 'attendance:holiday.messages.createByRegionError',
            ),
          );
          return;
        }

        showSuccess(
          translate(
            isEditMode
              ? 'attendance:holiday.messages.updateByRegionSuccess'
              : 'attendance:holiday.messages.createByRegionSuccess',
          ),
        );
        closeSidePanel();
        onSubmit?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      effectiveReadOnly,
      isEditMode,
      holidayByRegionData,
      setError,
      showApiError,
      showSuccess,
      closeSidePanel,
      onSubmit,
      translate,
    ],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      void rhfHandleSubmit(onValidSubmit)();
    },
    [rhfHandleSubmit, onValidSubmit],
  );

  return {
    isEditMode,
    holidayId: holidayByRegionData?.id ?? null,
    holidayTypeValue,
    holidayTypeOptions: holidayTypeCatalog.options,
    regionValue,
    regionOptions: regionCatalog.options,
    dateValue,
    reasonValue,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
    handleHolidayTypeChange,
    handleRegionChange,
    handleDateChange,
    handleReasonChange,
    handleSubmit,
  };
}

export default useHolidayByRegionFormPanel;